/**
 * Pre-purchase 1-Click Upsell — the browser half (feature 0042 lot D).
 *
 * Types, the one fetch, and the pure state helpers the popup chain runs on. Kept out of the block component so
 * the rules that decide what a buyer is charged can be unit-tested without a DOM.
 *
 * **No price rule lives here.** The server sends every variant's base price AND its offer price, both computed
 * by the same engine the checkout re-prices with, so switching variant is a lookup — never a discount replayed
 * in the browser, which is how a popup and a checkout start disagreeing by a centime.
 *
 * **Nothing here is trusted with money either.** The acceptance that travels to the server is
 * `(offerId, variantId, quantity)`; the totals below exist to be SHOWN, and the server re-derives them.
 */
/**
 * Fetch the chain for a product through the storefront's own origin.
 *
 * Returns null for EVERY negative outcome — no chain, an error, a timeout, an unparseable body. This runs on
 * the click that opens the order form, so the only acceptable failure is one the buyer never notices: the form
 * opens, and the shop keeps the sale it already had.
 */
export async function fetchUpsellChain(productId, opts = {}) {
    const doFetch = opts.fetchImpl ?? (typeof fetch !== 'undefined' ? fetch : undefined);
    if (!doFetch || !productId)
        return null;
    const params = new URLSearchParams({ productId });
    if (opts.store)
        params.set('store', opts.store);
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timer = controller
        ? setTimeout(() => controller.abort(), opts.timeoutMs ?? UPSELL_FETCH_TIMEOUT_MS)
        : null;
    try {
        const res = await doFetch(`/api/upsell?${params.toString()}`, {
            method: 'GET',
            headers: { Accept: 'application/json' },
            ...(controller ? { signal: controller.signal } : {}),
        });
        if (res.status !== 200)
            return null;
        const body = (await res.json());
        if (!body || !Array.isArray(body.offers) || body.offers.length === 0)
            return null;
        return body;
    }
    catch {
        return null;
    }
    finally {
        if (timer)
            clearTimeout(timer);
    }
}
/** The popup waits no longer than this before giving up and opening the form. */
export const UPSELL_FETCH_TIMEOUT_MS = 3_000;
/** The variant a popup should preselect: the default, falling back to the first available one. */
export function initialVariant(offer) {
    const variants = offer.product.variants;
    return (variants.find((v) => v.id === offer.product.defaultVariantId && v.available) ??
        variants.find((v) => v.available) ??
        null);
}
/**
 * Substitute the seller's `{discount}` token. An offer whose discount does not apply (a fixed amount in
 * another currency) sends an EMPTY label — and then the badge is not rendered at all rather than printed with
 * a hole in it, because "- " is a worse promise than no promise.
 */
export function discountTagText(template, discountLabel) {
    if (!discountLabel)
        return null;
    const text = (template || '{discount}').replace(/\{discount\}/g, discountLabel);
    return text.trim() ? text : null;
}
/** `m:ss` for the countdown — the shape a shopper reads without thinking. Never negative. */
export function formatCountdown(msLeft) {
    const total = Math.max(0, Math.floor(msLeft / 1000));
    const minutes = Math.floor(total / 60);
    const seconds = total % 60;
    return `${minutes}:${String(seconds).padStart(2, '0')}`;
}
/** Substitute the seller's `{time}` token in the timer copy. */
export function timerText(template, remaining) {
    return (template || '{time}').replace(/\{time\}/g, remaining);
}
/**
 * Add or replace an acceptance. Keyed by `offerId`, because an offer is accepted ONCE — a chain that let the
 * same offer land twice would send the server a body it refuses outright, losing the whole order over a
 * double click.
 */
/**
 * Start an offer's countdown the first time it is SHOWN, and never again (feature 0042 lot D, shared by
 * every surface that plays a chain since 0069 lot C).
 *
 * **The clock must start when the offer is SEEN, not when it was fetched.** In the embedded COD layout the
 * chain is prefetched from the buyer's first keystroke, sometimes minutes before they tap "Commander": at
 * fetch time a 10-minute timer showed 6 minutes on its first frame, or had already expired — which hides
 * the banner entirely. Idempotent by construction, so a re-render never restarts a timer already running,
 * and a buyer who closes and reopens the shell is not handed fresh urgency.
 *
 * Returns the SAME object when nothing changes, so it is safe to call from a state updater.
 */
export function seedDeadline(deadlines, offer, now = Date.now()) {
    if (!offer?.design.timer.enabled)
        return deadlines;
    if (deadlines[offer.offerId])
        return deadlines;
    return { ...deadlines, [offer.offerId]: now + offer.design.timer.minutes * 60_000 };
}
export function withAcceptance(accepted, line) {
    const without = accepted.filter((a) => a.offerId !== line.offerId);
    return [...without, line];
}
/** Remove one acceptance (the × in the order summary). */
export function withoutAcceptance(accepted, offerId) {
    return accepted.filter((a) => a.offerId !== offerId);
}
/** The wire shape of the accepted offers, or undefined when there is nothing to send. */
export function upsellSubmission(chain, accepted) {
    if (!chain || accepted.length === 0)
        return undefined;
    return {
        upsellId: chain.upsellId,
        version: chain.version,
        accepted: accepted.map((a) => ({
            offerId: a.offerId,
            variantId: a.variantId,
            quantity: a.quantity,
        })),
    };
}
/** What the accepted offers add to the displayed total, in cents. Display only. */
export function acceptedTotalCents(accepted) {
    return accepted.reduce((sum, a) => sum + a.unitCents * a.quantity, 0);
}
//# sourceMappingURL=upsell.js.map